import React, { useEffect, useRef, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, LayersControl } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";
import { EventResponseDto } from "../services/Dtos/EventsDtos";
import { getEventTypeHexColor } from "../helpers/eventTypeColors";

const { BaseLayer } = LayersControl;

// Custom Heatmap Renderer Component
const HeatmapRenderer: React.FC<{ data: any[]; map: L.Map | null }> = ({ data, map }) => {
  const heatmapLayerRef = useRef<any>(null);

  useEffect(() => {
    if (!map || !data || data.length === 0) return;

    try {
      // Remove existing heatmap layer if it exists
      if (heatmapLayerRef.current) {
        map.removeLayer(heatmapLayerRef.current);
      }

      // Create heatmap layer with leaflet.heat
      // @ts-ignore
      const heatmapLayer = L.heatLayer(data, {
        maxZoom: 18,
        max: 20,
        radius: 50,
        blur: 8,
        minOpacity: 0.3,
        gradient: {
          0.0: "#0000ff",
          0.15: "#00ff00",
          0.3: "#ffff00",
          0.5: "#ff8800",
          0.75: "#ff0000",
          1.0: "#800000",
        },
      }).addTo(map);

      heatmapLayerRef.current = heatmapLayer;

      return () => {
        if (heatmapLayerRef.current && map) {
          map.removeLayer(heatmapLayerRef.current);
        }
      };
    } catch (error) {
      console.error("Error creating heatmap:", error);
    }
  }, [data, map]);

  return null;
};

// Fix for default marker icons in React Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

interface AnalyticsFilterMapProps {
  events: EventResponseDto[];
  showAddressFrom?: boolean;
  zoomToCoordinates?: { lat: number; lng: number; zoom?: number } | null;
  selectedStateCode?: string | null;
  showHeatmap?: boolean;
}

const AnalyticsFilterMap: React.FC<AnalyticsFilterMapProps> = ({
  events,
  showAddressFrom = false,
  zoomToCoordinates = null,
  selectedStateCode = null,
  showHeatmap = false,
}) => {
  const mapRef = useRef<L.Map | null>(null);
  const mapInitializedRef = useRef(false);

  // Initialize zoom level from localStorage  
  const [initialZoom] = useState(() => {
    try {
      const stored = localStorage.getItem("analyticsMapZoom");
      const zoomLevel = stored ? parseInt(stored, 10) : 5;
      console.log("[AnalyticsMap Zoom] Loaded from localStorage:", zoomLevel, "(stored value:", stored, ")");
      return zoomLevel;
    } catch {
      console.log("[AnalyticsMap Zoom] Failed to read from localStorage, using default: 4");
      return 5;
    }
  });

  const saveZoomToStorage = (zoom: number) => {
    try {
      localStorage.setItem("analyticsMapZoom", zoom.toString());
    } catch {
      // Silently fail if localStorage is unavailable
    }
  };

  // Filter events that have valid coordinates for addressTo
  const eventsWithCoordinates = events.filter(
    (event) =>
      event.addressTo?.latitude !== undefined &&
      event.addressTo?.longitude !== undefined &&
      !isNaN(event.addressTo.latitude) &&
      !isNaN(event.addressTo.longitude) &&
      event.addressTo.latitude !== 0 &&
      event.addressTo.longitude !== 0
  );

  // Filter events that have valid coordinates for addressFrom
  const eventsWithFromCoordinates = useMemo(
    () =>
      showAddressFrom
        ? events.filter(
            (event) =>
              event.addressFrom?.latitude !== undefined &&
              event.addressFrom?.longitude !== undefined &&
              !isNaN(event.addressFrom.latitude) &&
              !isNaN(event.addressFrom.longitude) &&
              event.addressFrom.latitude !== 0 &&
              event.addressFrom.longitude !== 0
          )
        : [],
    [showAddressFrom, events]
  );

  // Filter events that have valid coordinates for both addressFrom and addressTo (for polylines/arrows)
  const eventsWithValidPolylines = useMemo(
    () =>
      showAddressFrom
        ? events.filter(
            (event) =>
              event.addressFrom?.latitude !== undefined &&
              event.addressFrom?.longitude !== undefined &&
              event.addressTo?.latitude !== undefined &&
              event.addressTo?.longitude !== undefined &&
              !isNaN(event.addressFrom.latitude) &&
              !isNaN(event.addressFrom.longitude) &&
              !isNaN(event.addressTo.latitude) &&
              !isNaN(event.addressTo.longitude) &&
              event.addressFrom.latitude !== 0 &&
              event.addressFrom.longitude !== 0 &&
              event.addressTo.latitude !== 0 &&
              event.addressTo.longitude !== 0
          )
        : [],
    [showAddressFrom, events]
  );

  const previousZoomRef = useRef<number>(5);

  const createColoredIcon = (eventType: string, isFromAddress: boolean = false): L.Icon => {
    const color = getEventTypeHexColor(eventType);
    return new L.Icon({
      iconUrl:
        "data:image/svg+xml;base64," +
        btoa(`
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 41">
          <path fill="${color}" stroke="#fff" stroke-width="2" d="M12.5 0C5.596 0 0 5.596 0 12.5c0 8.437 12.5 28.5 12.5 28.5S25 20.937 25 12.5C25 5.596 19.404 0 12.5 0z"/>
          <circle cx="12.5" cy="12.5" r="6" fill="#fff"/>
          ${isFromAddress ? '<text x="12.5" y="16" text-anchor="middle" font-size="12" fill="' + color + '" font-weight="bold">F</text>' : ""}
        </svg>
      `),
      shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41],
    });
  };

  // Generate heatmap data points from events
  const heatmapData = useMemo(
    () =>
      eventsWithCoordinates.map((event) => [
        event.addressTo.latitude!,
        event.addressTo.longitude!,
        1, // intensity (all points weighted equally)
      ]),
    [eventsWithCoordinates]
  );

  const formatEventType = (eventType: string): string => {
    return eventType
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const formatAddress = (
    street1: string,
    street2: string | undefined,
    city: string,
    state: string,
    zipCode: string
  ): string => {
    const street = street2 ? `${street1}, ${street2}` : street1;
    return `${street}, ${city}, ${state} ${zipCode}`;
  };

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Fit bounds to show all events (only if no state is selected)
  useEffect(() => {
    if (!mapRef.current || mapInitializedRef.current === false) {
      mapInitializedRef.current = true;
    }

    // Skip fitBounds if a state was explicitly selected - let the zoom-to-state effect handle it
    if (selectedStateCode) {
      console.log(`[AnalyticsMap ZOOM] FitBounds skipped - state ${selectedStateCode} was selected`);
      return;
    }

    if (eventsWithCoordinates.length > 0 || eventsWithFromCoordinates.length > 0) {
      const allCoordinates = [
        ...eventsWithCoordinates.map(
          (event) =>
            [event.addressTo.latitude!, event.addressTo.longitude!] as [number, number]
        ),
        ...eventsWithFromCoordinates.map(
          (event) =>
            [event.addressFrom!.latitude!, event.addressFrom!.longitude!] as [number, number]
        ),
      ];

      if (allCoordinates.length > 0 && mapRef.current) {
        const bounds = L.latLngBounds(allCoordinates);
        mapRef.current.fitBounds(bounds, {
          padding: [50, 50],
          maxZoom: 12,
        });
      }
    }
  }, [eventsWithCoordinates, eventsWithFromCoordinates, selectedStateCode]);

  // Effect to zoom to selected state coordinates
  useEffect(() => {
    if (zoomToCoordinates && mapRef.current) {
      const targetZoom = zoomToCoordinates.zoom || 7; // Default zoom level for state view
      console.log(
        `[AnalyticsMap ZOOM] STATE - Zooming to lat=${zoomToCoordinates.lat.toFixed(6)}, lng=${zoomToCoordinates.lng.toFixed(6)}, zoom=${targetZoom}`
      );
      mapRef.current.setView(
        [zoomToCoordinates.lat, zoomToCoordinates.lng],
        targetZoom,
        { animate: true, duration: 0.5 }
      );
      saveZoomToStorage(targetZoom);
    }
  }, [zoomToCoordinates]);

  // Effect to track zoom changes and persist to localStorage
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const handleZoomEnd = () => {
      const currentZoom = map.getZoom();
      const center = map.getCenter();
      const lat = center.lat.toFixed(6);
      const lng = center.lng.toFixed(6);
      console.log(
        `[AnalyticsMap ZOOM] MANUAL - User zoom ended: zoom=${currentZoom}, lat=${lat}, lng=${lng}`
      );
      saveZoomToStorage(currentZoom);
      previousZoomRef.current = currentZoom;
    };

    map.on("zoomend", handleZoomEnd);
    return () => {
      map.off("zoomend", handleZoomEnd);
    };
  }, []);

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Analytics Filter Map</h5>
        <div className="d-flex gap-3 align-items-center">
          <span className="badge bg-light text-primary">
            <i className="bi bi-geo-alt-fill me-1" style={{ color: "#3388ff" }}></i>
            To: {eventsWithCoordinates.length}
          </span>
          {showAddressFrom && (
            <span className="badge bg-light text-success">
              <i className="bi bi-geo-alt-fill me-1" style={{ color: "#28a745" }}></i>
              From: {eventsWithFromCoordinates.length}
            </span>
          )}
        </div>
      </div>
      <div className="card-body p-0">
        <div style={{ height: "600px", width: "100%" }}>
          <MapContainer
            center={[39.8283, -98.5795]}
            zoom={initialZoom}
            style={{ height: "100%", width: "100%" }}
            ref={mapRef}
          >
            <LayersControl position="topright">
              <BaseLayer checked name="Street Map">
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
              </BaseLayer>
              <BaseLayer name="Satellite">
                <TileLayer
                  attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  maxZoom={19}
                />
              </BaseLayer>
              <BaseLayer name="Terrain">
                <TileLayer
                  attribution='Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a>'
                  url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
                  maxZoom={17}
                />
              </BaseLayer>
            </LayersControl>

            {/* Heatmap layer when showHeatmap is enabled */}
            {showHeatmap && <HeatmapRenderer data={heatmapData} map={mapRef.current} />}

            {/* Polylines/Arrows connecting addressFrom to addressTo - Only show when not in heatmap mode */}
            {!showHeatmap &&
              eventsWithValidPolylines.map((event) => (
                <Polyline
                  key={`polyline-${event.id}`}
                  positions={[
                    [event.addressFrom!.latitude!, event.addressFrom!.longitude!],
                    [event.addressTo.latitude!, event.addressTo.longitude!],
                  ]}
                  pathOptions={{
                    color: getEventTypeHexColor(event.eventType),
                    weight: 3,
                    opacity: 0.7,
                    dashArray: "5, 5",
                  }}
                />
              ))}

            {/* Address To Markers (Colored by event type) OR Heatmap */}
            {!showHeatmap ? (
              eventsWithCoordinates.map((event) => (
                <Marker
                  key={`to-${event.id}`}
                  position={[event.addressTo.latitude!, event.addressTo.longitude!]}
                  icon={createColoredIcon(event.eventType, false)}
                >
                  <Popup maxWidth={300}>
                    <div style={{ minWidth: "250px" }}>
                      <h6 className="text-primary fw-bold mb-2">
                        <i className="bi bi-geo-alt-fill me-2"></i>
                        Address To - {formatEventType(event.eventType)}
                      </h6>
                      <div className="mb-2">
                        <strong>Tracking:</strong> {event.trackingNumber}
                      </div>
                      <div className="mb-2">
                        <strong>Company:</strong> {event.company.name}
                      </div>
                      <div className="mb-2">
                        <strong>Address:</strong>
                        <br />
                        {formatAddress(
                          event.addressTo.street1,
                          event.addressTo.street2,
                          event.addressTo.city,
                          event.addressTo.state,
                          event.addressTo.zipCode
                        )}
                      </div>
                      {event.addressTo.metroCode && (
                        <div className="mb-2">
                          <strong>Metro Code:</strong> {event.addressTo.metroCode}
                        </div>
                      )}
                      <div className="mb-2">
                        <strong>Event Time:</strong>
                        <br />
                        {formatDate(event.eventTimestamp)}
                      </div>
                      <div className="text-muted small">
                        <strong>Coordinates:</strong>
                        <br />
                        {event.addressTo.latitude?.toFixed(6)}, {event.addressTo.longitude?.toFixed(6)}
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))
            ) : null}

            {/* Address From Markers (Colored by event type with 'F' indicator) - Only show when not in heatmap mode */}
            {!showHeatmap &&
              showAddressFrom &&
              eventsWithFromCoordinates.map((event) => (
                <Marker
                  key={`from-${event.id}`}
                  position={[event.addressFrom!.latitude!, event.addressFrom!.longitude!]}
                  icon={createColoredIcon(event.eventType, true)}
                >
                  <Popup maxWidth={300}>
                    <div style={{ minWidth: "250px" }}>
                      <h6 className="text-success fw-bold mb-2">
                        <i className="bi bi-geo-alt-fill me-2"></i>
                        Address From - {formatEventType(event.eventType)}
                      </h6>
                      <div className="mb-2">
                        <strong>Tracking:</strong> {event.trackingNumber}
                      </div>
                      <div className="mb-2">
                        <strong>Company:</strong> {event.company.name}
                      </div>
                      <div className="mb-2">
                        <strong>Address:</strong>
                        <br />
                        {formatAddress(
                          event.addressFrom!.street1,
                          event.addressFrom!.street2,
                          event.addressFrom!.city,
                          event.addressFrom!.state,
                          event.addressFrom!.zipCode
                        )}
                      </div>
                      {event.addressFrom!.metroCode && (
                        <div className="mb-2">
                          <strong>Metro Code:</strong> {event.addressFrom!.metroCode}
                        </div>
                      )}
                      <div className="mb-2">
                        <strong>Event Time:</strong>
                        <br />
                        {formatDate(event.eventTimestamp)}
                      </div>
                      <div className="text-muted small">
                        <strong>Coordinates:</strong>
                        <br />
                        {event.addressFrom!.latitude?.toFixed(6)},{" "}
                        {event.addressFrom!.longitude?.toFixed(6)}
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
          </MapContainer>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsFilterMap;
